import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { SafeUrl } from '@angular/platform-browser';
import { TranslocoService } from '@ngneat/transloco';
import { Logger } from 'aws-amplify';
import cleanDeep from 'clean-deep';
import { fabric } from 'fabric';
import { EMPTY, Observable, forkJoin, of } from 'rxjs';
import { catchError, delay, map, switchMap, tap } from 'rxjs/operators';
import { Doctor, NameFormat, QuestionnaireType, TemperatureUnit } from 'src/API';
import { ClinicService } from 'src/app/core/api/clinic.service';
import { noSort } from 'src/app/core/api/model-utils';
import { QuestionnaireRequestService } from 'src/app/core/api/questionnaire-request.service';
import { AssessmentFormService } from 'src/app/encounters/assessment-form.service';
import {
  ContentType,
  ImageToDataUriPipe
} from 'src/app/shared/shared-pipes/image-to-data-uri.pipe';
import { AssessmentBody } from '../../econsult/assessment-body/assessment-body.model';
import { S3AttachmentService, S3Object } from '../attachments/s3-attachment.service';
import { leftEyeKey, rightEyeKey, symptomMethodKey } from '../form.model';
import { DrugFormat } from '../medication-lookup/drug.pipe';
import { SvgPipe } from '../shared-pipes/svg.pipe';
import { SchemaService } from '../symptoms/services/schema/schema.service';
import { SymptomModes } from '../symptoms/symptom.model';
import { ClinicSetupService } from './../../logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { LayoutsService } from './../../logged-in-navbar/clinic-setup-modal/layouts/layouts.service';
import { SymptomTypes } from './../symptoms/symptom.model';
import { defaultLogoUri } from './default-logo-uri.model';
import { UrlToDataUriMap } from './pdf-export-treatment/pdf-export-treatment.component';

const componentStyle = require('./dry-eye-pdf-export.component.css');
const dryEyeCategoryComponentStyle = require('./pdf-export-dry-eye-category/pdf-export-dry-eye-category.component.css');
const googleChartsComponentStyle = require('./pdf-export-donut-chart/pdf-export-donut-chart.component.css');
const pdfSummaryComponentStyle = require('./pdf-summary-export/pdf-summary-export.component.css');
const pdfCustomComponentStyle = require('./pdf-custom-export/pdf-custom-export.component.scss');

export type PDFType = 'simple' | 'detailed' | 'summary' | 'custom';

@Component({
  selector: 'csi-dry-eye-pdf-export',
  templateUrl: './dry-eye-pdf-export.component.html',
  styleUrls: ['./dry-eye-pdf-export.component.css'],
  providers: [SvgPipe, ImageToDataUriPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DryEyePdfExportComponent implements OnInit, OnDestroy {
  customWrapper!: ElementRef<HTMLElement>;
  public TemperatureUnit = TemperatureUnit;
  private _data: AssessmentBody;
  private _clinicLogo: S3Object;
  public defaultLogo = defaultLogoUri;
  public medicalHistoryShowFields = [];

  clinicLogoDataUri: SafeUrl;
  eyeDropImageDataUris: UrlToDataUriMap = {};

  public readonly SymptomModes = SymptomModes;

  public readonly NameFormat = NameFormat;
  public readonly DrugFormat = DrugFormat;
  public readonly SymptomTypes = SymptomTypes;
  public localMedicalHistoryFields = [];
  public localMedicationFields = [];
  public interpretation: string[] = [];
  public riskFactors: string[] = [];
  public riskFactorTitle: string;
  public htmlClassExtractor: { [key: string]: string } = {
    interpretation: 'interpretation-data-container',
    riskFactor: 'risk-factors-data-container'
  };

  private readonly RemovedTextDecoration = `h4[style*='underline']`;
  public isLoaded = false;
  public loaded = false;

  public elementColumnConfigp = {
    riskFactor: { columns: 1 }
  };
  public elementColumnConfigMap: {
    riskFactor: { [key: string]: number };
  } = { riskFactor: {} };

  private logger: Logger = new Logger('DryEyePdfExportComponent');
  public medHistForm: any;

  public drawnSignatureDataUrl: string | null = null;
  @Input() signingDoctor: Partial<Doctor>;

  @Input('data')
  set data(value: AssessmentBody) {
    this.logger.debug('setting data');
    // remove empty objects so we can use ngIf to test for values
    this._data = cleanDeep(value) as AssessmentBody;

    this.medHistForm = this._data.medicalHistoryForm;
  }

  get data(): AssessmentBody {
    return this._data;
  }

  @Input() translateObject: any;

  @Input('clinicLogo')
  set clinicLogo(value: S3Object) {
    if (this.clinicLogo !== value) {
      this._clinicLogo = value;
      this.renderClinicLogo().subscribe();
    }
  }

  @Input() pdfType: PDFType;
  @Input() hideDataForFaxing = false;

  @Input() lang: string;
  public isAssessmentLocked: boolean;
  public isAtRisk: boolean;

  constructor(
    private hostElementRef: ElementRef,
    public schemaService: SchemaService,
    public layoutsService: LayoutsService,
    private imageToDataUriPipe: ImageToDataUriPipe,
    private s3AttachmentService: S3AttachmentService,
    private httpClient: HttpClient,
    private changeDetector: ChangeDetectorRef,
    private clinicService: ClinicService,
    public clinicSetupService: ClinicSetupService,
    public translocoService: TranslocoService,
    public assessmentFormService: AssessmentFormService,
    public questionnaireRequestService: QuestionnaireRequestService
  ) {}
  ngOnDestroy(): void {}

  ngOnInit() {
    const layout = this.layoutsService.allLayoutsById[this.data.dryEyeForm.assessmentMethod];
    this.isAssessmentLocked = this.assessmentFormService.isAssessmentLocked;

    //Current ocular medications and drops key: currentOcularMedicationsFreeText
    //Past ocular medications and drops key: pastOcularMedicationsFreeText
    //Should always be hidden as all medication are linked to the medication section

    this.localMedicationFields = [
      'patientEnteredMedicationsFreeText',
      'currentOcularMedicationsFreeText',
      'pastOcularMedicationsFreeText',
      'medications'
    ];

    this.localMedicalHistoryFields = ((layout && layout.medicalHistoryLayout.show) || []).filter(
      field => !this.localMedicationFields.includes(field)
    );

    const scores = JSON.stringify({
      [QuestionnaireType.SPEED]: this.data.dryEyeForm['speed'],
      [QuestionnaireType.SPEEDII]: this.data.dryEyeForm['speedii'],
      [QuestionnaireType.DEQ]: this.data.dryEyeForm['deq'],
      [QuestionnaireType.OSDI]: this.data.dryEyeForm['osdi'],
      [QuestionnaireType.FITZPATRICK]: this.data.dryEyeForm['fitzpatrick'],
      [QuestionnaireType.OSDI6]: this.data.dryEyeForm['osdi6']
    });
    this.isAtRisk = this.questionnaireRequestService.isAtRisk(scores);
  }

  ngAfterViewInit() {
    if (this.pdfType === 'summary' || this.pdfType === 'custom') {
      if (this._data.assessmentForm !== undefined) {
        // this.extractHtmlDom(this._data.assessmentForm.impressions, true);
      }
    }
  }

  parseValue(val: any) {
    if (typeof val === 'object') {
      return Object.entries(val)
        .map(([k, v]) => `${k[0].toUpperCase() + k.substring(1)}: ${v}`)
        .join(', ');
    }
    return val;
  }

  get clinicLogo(): S3Object {
    return this._clinicLogo;
  }

  get html(): Observable<string> {
    const layout = this.layoutsService.allLayoutsById[this.data.dryEyeForm.assessmentMethod];

    this.medicalHistoryShowFields = (layout && layout.medicalHistoryLayout.show) || [];

    this.logger.debug('rendering pdf html');
    return this.render().pipe(
      delay(100),
      tap(() => {
        this.changeDetector.detectChanges();
      }),
      map(() => {
        this.changeDetector.detectChanges();
        this.logger.debug('calling innerHTMLWithStyle');
        const html = this.innerHTMLWithStyle;
        return html;
      }),
      catchError((err, caught) => {
        console.log(err, caught);
        return EMPTY;
      })
    );
  }

  private render(): Observable<boolean> {
    this.logger.debug('render() called');
    const renderClinicLogo$: Observable<boolean> = this.clinicLogoDataUri
      ? of(true)
      : this.renderClinicLogo();

    let allEyeDrops = null;
    if (this.data && this.data.assessmentForm) {
      allEyeDrops = [
        ...(this.data.assessmentForm.recommendedEyeDrops || []),
        ...(this.data.assessmentForm.recommendedRxEyeDrops || []),
        ...(this.data.assessmentForm.recommendedExercises || []),
        ...(this.data.assessmentForm.recommendedProcedures || [])
      ].filter(eyeDrop => !!eyeDrop);
    }

    const renderEyeDropImages$: Observable<boolean> = of(allEyeDrops).pipe(
      switchMap(eyeDrops => {
        const dataUris$: Observable<SafeUrl>[] = [];
        if (eyeDrops && eyeDrops.length > 0) {
          eyeDrops.forEach(eyeDrop => {
            if (!!eyeDrop.image) {
              dataUris$.push(
                this.getImageDataUri(eyeDrop.image).pipe(
                  tap(safeUrl => {
                    this.logger.debug(`setting safeUrl for ${eyeDrop.image}`);
                    this.eyeDropImageDataUris[eyeDrop.image] = safeUrl;
                  })
                )
              );
            }
          });
        }
        return dataUris$.length > 0
          ? forkJoin([...dataUris$]).pipe(
              map(() => {
                this.logger.debug('got all safe urls');
                return true;
              })
            )
          : of(true);
      })
    );

    const renderDrawnSignature$: Observable<boolean> = of(this.data).pipe(
      switchMap(data => {
        const signOff = data.interactionSignoffGroup;
        if (signOff && signOff['signatureType'] === 'drawn' && signOff['drawnSignature']) {
          return this.convertSignatureToDataUrl(signOff['drawnSignature']).pipe(
            tap(dataUrl => {
              this.drawnSignatureDataUrl = dataUrl;
              this.changeDetector.detectChanges();
            }),
            map(() => true),
            catchError(error => {
              console.error('Signature render error:', error);
              return of(true);
            })
          );
        }
        return of(true);
      })
    );

    this.logger.debug('returning fork join');

    return forkJoin([renderClinicLogo$, renderEyeDropImages$, renderDrawnSignature$]).pipe(
      tap(() => this.changeDetector.detectChanges()),
      map(() => true)
    );
  }

  private convertSignatureToDataUrl(signatureJson: any): Observable<string> {
    return new Observable(observer => {
      let jsonObj = signatureJson;
      if (typeof jsonObj === 'string') {
        try {
          jsonObj = JSON.parse(jsonObj);
        } catch {
          return observer.error(new Error('Malformed JSON'));
        }
      }
      if (!Array.isArray(jsonObj.objects)) {
        return observer.error(new Error('Missing objects[] in JSON'));
      }

      const canvas = new fabric.Canvas(document.createElement('canvas'), {
        width: 550,
        height: 150,
        backgroundColor: '#fff',
        enableRetinaScaling: false
      });

      try {
        canvas.loadFromJSON(jsonObj, () => {
          canvas.getObjects().forEach(obj => {
            obj.selectable = obj.evented = false;
            if (obj.type === 'path' && !obj.stroke) {
              obj.set('stroke', '#000');
            }
          });

          canvas.renderAll();
          const dataUrl = canvas.toDataURL({ format: 'png', quality: 1, multiplier: 2 });
          observer.next(dataUrl);
          observer.complete();
        });
      } catch (e) {
        observer.error(new Error(`Fabric load failed: ${e.message}`));
      }
    });
  }

  private renderClinicLogo(): Observable<boolean> {
    this.logger.debug('rendering clinic logo');
    return of(this.clinicLogo).pipe(
      switchMap(logo => (logo ? this.s3AttachmentService.s3ObjectToUri(logo) : of(null))),
      map(logoDataUri => {
        this.logger.debug('setting logo data uri');
        this.clinicLogoDataUri = logoDataUri;

        return true;
      })
    );
  }

  private getImageDataUri(url: string): Observable<SafeUrl> {
    this.logger.debug(`getImageDataUri: getting ${url}`);
    return this.httpClient.get(url, { responseType: 'blob' }).pipe(
      switchMap(imageBlob => {
        this.logger.debug('got image blob');
        return this.imageToDataUriPipe.transform(imageBlob, imageBlob.type as ContentType, true);
      })
    );
  }

  private get innerHTMLWithStyle(): string {
    this.logger.debug('setting inner html');
    return this.hostElement.innerHTML.replace(
      '</head>',
      `<style>${componentStyle.default}
       ${dryEyeCategoryComponentStyle.default}
       ${googleChartsComponentStyle.default}
        ${pdfSummaryComponentStyle.default}
         ${pdfCustomComponentStyle.default}</style></head>`
    );
  }

  public hasValue(value: string): boolean {
    if (value === null || value === undefined) {
      return false;
    } else {
      return value.toString().length > 0;
    }
  }

  get assessmentDate(): Date {
    // TODO: Support custom assessment date
    return new Date();
  }

  public shouldShowSymptom(symptomKey: string): boolean {
    return (
      (this.isSimpleMode && this.isSimpleSymptom(symptomKey)) ||
      (this.isAdvancedMode && this.isAdvancedSymptom(symptomKey))
    );
  }

  public shouldShowSymptomMethod(symptomKey: string): { right: boolean; left: boolean } {
    return {
      right:
        this.schemaService.symptomMap[symptomKey].type === SymptomTypes.Method &&
        this.hasValue(rightEyeKey(symptomMethodKey(symptomKey))) &&
        this.hasValue(rightEyeKey(symptomKey)),

      left:
        this.schemaService.symptomMap[symptomKey].type === SymptomTypes.Method &&
        this.hasValue(leftEyeKey(symptomMethodKey(symptomKey))) &&
        this.hasValue(leftEyeKey(symptomKey))
    };
  }

  public isSimpleSymptom(symptomKey: string): boolean {
    return this.schemaService.symptomMap[symptomKey].modes.indexOf(SymptomModes.Simple) !== -1;
  }

  public isAdvancedSymptom(symptomKey: string): boolean {
    return this.schemaService.symptomMap[symptomKey].modes.indexOf(SymptomModes.Advanced) !== -1;
  }

  get isSimpleMode(): boolean {
    return this.data.dryEyeForm.assessmentMethod === SymptomModes.Simple;
  }

  get isAdvancedMode(): boolean {
    return this.data.dryEyeForm.assessmentMethod === SymptomModes.Advanced;
  }

  private get hostElement(): HTMLElement {
    return this.hostElementRef.nativeElement;
  }
  public noSort = noSort;

  public extractHtmlDom(htmlBody: string, removeFromHtml = false) {
    if (!htmlBody) {
      return;
    }
    const parser = new DOMParser();
    const dom = parser.parseFromString(htmlBody, 'text/html');

    const interpEl = Array.from(dom.querySelectorAll('.interpretation-data-container'));
    console.log(interpEl);

    if (interpEl.length > 0) {
      interpEl.forEach(interElement => {
        console.log(interElement);
        const title = interElement.querySelector('interpretation-title');
        if (title) title.remove();
        this.interpretation.length > 0
          ? this.interpretation.push(
              `<div class='summary-interpretation-container'>${interElement.innerHTML}</div>`
            )
          : this.interpretation.push(interElement.innerHTML);
        interElement.remove();
      });
    }

    const riskElList = dom.querySelectorAll('.risk-factors-data-container');

    if (riskElList.length > 0) {
      riskElList.forEach((riskElement, index) => {
        const titleEl = riskElement.querySelector('.risk-factor-title');
        this.riskFactorTitle = titleEl ? titleEl.outerHTML : '';
        if (titleEl) titleEl.remove();
        this.riskFactors.length > 0
          ? this.riskFactors.push(
              `<div class='summary-interpretation-container'>${riskElement.innerHTML}</div>`
            )
          : this.riskFactors.push(riskElement.innerHTML);
        console.log(riskElement);
        this.riskFactorColumns(riskElement.innerHTML, index);
        riskElement.remove();
      });
    }
    this._data.assessmentForm.impressions = new XMLSerializer().serializeToString(dom);
  }

  private riskFactorColumns(riskFactorElement: string, index: number) {
    this.elementColumnConfigMap.riskFactor[index] =
      (riskFactorElement.match(/<\/li>/gi) || []).length >= 10 ? 2 : 1;
  }
}
